<template>
  <div>
    <!-- Başlık -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-5">
      <div class="d-flex align-center gap-3">
        <v-btn icon variant="text" size="small" @click="$router.back()">
          <span class="material-symbols-outlined">arrow_back</span>
        </v-btn>
        <div>
          <h4 class="mb-0">Kullanıcı Soruları</h4>
          <div v-if="data" class="text-caption text-medium-emphasis mt-1">
            <span class="material-symbols-outlined" style="font-size:14px;vertical-align:middle">person</span>
            {{ data.user.name }}
            <span v-if="data.user.username">({{ data.user.username }})</span>
          </div>
        </div>
      </div>

      <!-- Period Seçici -->
      <div class="d-flex align-center flex-wrap gap-2">
        <v-btn-toggle
          v-model="period"
          mandatory
          color="primary"
          variant="outlined"
          density="compact"
          @update:model-value="onPeriodChange"
        >
          <v-btn value="today">Bugün</v-btn>
          <v-btn value="yesterday">Dün</v-btn>
          <v-btn value="week">Son 7 Gün</v-btn>
          <v-btn value="month">Son 30 Gün</v-btn>
          <v-btn value="custom">Özel</v-btn>
        </v-btn-toggle>

        <template v-if="period === 'custom'">
          <v-text-field
            v-model="customFrom"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            style="width:150px"
            :max="customTo || today"
            @update:model-value="onCustomRangeChange"
          />
          <span class="text-medium-emphasis">—</span>
          <v-text-field
            v-model="customTo"
            type="date"
            density="compact"
            variant="outlined"
            hide-details
            style="width:150px"
            :min="customFrom"
            :max="today"
            @update:model-value="onCustomRangeChange"
          />
        </template>
      </div>
    </div>

    <!-- Hata -->
    <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <!-- Loading -->
    <template v-if="loading">
      <v-row>
        <v-col v-for="n in 3" :key="n" cols="12" md="4">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="table" class="mt-4" />
    </template>

    <template v-else-if="data">
      <!-- KPI Kartları -->
      <v-row class="mb-4">
        <v-col cols="12" sm="4">
          <v-card class="trezo-card bg-white border-0 shadow-none pa-4" style="border-left:4px solid #1a5f4a">
            <div class="text-caption text-medium-emphasis mb-1">Toplam Soru</div>
            <div class="text-h5 font-weight-bold" style="color:#1a5f4a">{{ data.total }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="trezo-card bg-white border-0 shadow-none pa-4" style="border-left:4px solid #c9a227">
            <div class="text-caption text-medium-emphasis mb-1">Ortalama Puan</div>
            <div class="d-flex align-center gap-1">
              <div class="text-h5 font-weight-bold" style="color:#c9a227">
                {{ data.avg_rating ?? '—' }}
              </div>
              <span v-if="data.avg_rating" class="material-symbols-outlined" style="color:#c9a227;font-size:20px">star</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="trezo-card bg-white border-0 shadow-none pa-4" style="border-left:4px solid #6b7a72">
            <div class="text-caption text-medium-emphasis mb-1">Puanlama Durumu</div>
            <div class="text-body-2 mt-1">
              <span class="font-weight-medium" style="color:#1a5f4a">{{ data.rated_count }} puanlı</span>
              <span class="text-medium-emphasis mx-1">·</span>
              <span class="text-medium-emphasis">{{ data.unrated_count }} puansız</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tablo -->
      <v-card class="trezo-card bg-white border-0 shadow-none">
        <div class="v-card-header">
          <h5 class="mb-0">Sorular</h5>
        </div>
        <div v-if="!data.questions.length" class="text-center text-medium-emphasis py-8">
          Bu dönemde soru bulunamadı.
        </div>
        <v-data-table
          v-else
          :headers="headers"
          :items="data.questions"
          density="compact"
          :items-per-page="perPage"
          :items-per-page-options="[20, 50, 100]"
          @click:row="(_: Event, row: any) => openDetail(row.item)"
          style="cursor:pointer"
        >
          <template #item.question="{ item }">
            <span class="question-cell">{{ item.question }}</span>
          </template>

          <template #item.asked_at="{ item }">
            <span class="text-caption text-medium-emphasis">{{ formatDate(item.asked_at) }}</span>
          </template>

          <template #item.rating="{ item }">
            <span v-if="item.rating" class="d-flex align-center">
              <span
                v-for="i in 5" :key="i"
                class="material-symbols-outlined"
                :style="{ fontSize: '15px', color: i <= item.rating ? '#c9a227' : '#ddd' }"
              >star</span>
            </span>
            <span v-else class="text-medium-emphasis text-caption">—</span>
          </template>

          <template #item.comment="{ item }">
            <span v-if="item.comment" class="text-caption">{{ item.comment }}</span>
            <span v-else class="text-medium-emphasis text-caption">—</span>
          </template>
        </v-data-table>

        <!-- Manuel Sayfalama -->
        <div v-if="data.last_page > 1" class="d-flex justify-center align-center gap-3 py-3">
          <v-btn
            variant="outlined"
            density="compact"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >Önceki</v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ currentPage }} / {{ data.last_page }}
          </span>
          <v-btn
            variant="outlined"
            density="compact"
            :disabled="currentPage >= data.last_page"
            @click="goToPage(currentPage + 1)"
          >Sonraki</v-btn>
        </div>
      </v-card>
    </template>

    <!-- Soru/Cevap Dialog -->
    <v-dialog v-model="dialog" max-width="800" scrollable>
      <v-card v-if="selected">
        <v-card-title class="d-flex align-center pa-4 pb-2">
          <span class="material-symbols-outlined mr-2" style="color:#1a5f4a">forum</span>
          <span class="text-body-1 font-weight-medium">Soru & Cevap Detayı</span>
          <v-spacer />
          <v-btn icon variant="text" @click="dialog = false">
            <span class="material-symbols-outlined">close</span>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4" style="max-height:70vh;overflow-y:auto">
          <!-- Meta -->
          <div class="d-flex align-center mb-3 text-caption text-medium-emphasis">
            <span class="material-symbols-outlined mr-1" style="font-size:16px">schedule</span>
            <span>{{ formatDate(selected.asked_at) }}</span>
            <template v-if="selected.rating">
              <span class="mx-2">·</span>
              <span
                v-for="i in 5" :key="i"
                class="material-symbols-outlined"
                :style="{ fontSize: '15px', color: i <= selected.rating ? '#c9a227' : '#ddd' }"
              >star</span>
            </template>
            <template v-if="selected.comment">
              <span class="mx-2">·</span>
              <span class="font-italic">{{ selected.comment }}</span>
            </template>
          </div>

          <!-- Soru -->
          <v-sheet color="#f5f7f6" rounded class="pa-3 mb-4">
            <div class="text-caption text-medium-emphasis mb-1 d-flex align-center">
              <span class="material-symbols-outlined mr-1" style="font-size:15px">help</span>
              SORU
            </div>
            <p class="text-body-2 mb-0" style="white-space:pre-wrap">
              {{ selected.question_full || selected.question }}
            </p>
          </v-sheet>

          <!-- Cevap -->
          <v-sheet color="#f0f7f4" rounded class="pa-3">
            <div class="text-caption text-medium-emphasis mb-2 d-flex align-center">
              <span class="material-symbols-outlined mr-1" style="font-size:15px">smart_toy</span>
              MAHAI CEVABI
            </div>
            <!-- Cevap backend'de zaten HTML olarak kaydediliyor; tekrar
                 markdown parse etme (çift işleme HTML'i bozar). -->
            <div
              v-if="selected.answer"
              class="text-body-2 answer-content"
              v-html="selected.answer"
            />
            <span v-else class="text-medium-emphasis text-caption">Cevap bulunamadı.</span>
          </v-sheet>

          <!-- Dayanak Belgeler -->
          <div v-if="selected.sources && selected.sources.length" class="mt-4">
            <div class="text-caption text-medium-emphasis mb-2 d-flex align-center">
              <span class="material-symbols-outlined mr-1" style="font-size:15px">menu_book</span>
              DAYANAK BELGELER
            </div>
            <div v-for="grp in sourceGroups(selected.sources)" :key="grp.key" class="mb-3">
              <div class="text-caption font-weight-medium mb-1" style="color:#1a5f4a">{{ grp.title }}</div>
              <v-sheet
                v-for="(src, i) in grp.items"
                :key="grp.key + '-' + i"
                color="#f5f7f6"
                rounded
                class="pa-2 mb-1 d-flex align-center gap-2"
              >
                <div class="flex-grow-1" style="min-width:0">
                  <div v-if="kunyeCourt(src)" class="text-caption text-medium-emphasis">{{ kunyeCourt(src) }}</div>
                  <div v-if="kunyeNumbers(src)" class="text-caption" style="font-family:monospace">{{ kunyeNumbers(src) }}</div>
                  <div class="text-body-2">
                    {{ src.yasa_adi || formatSourceName(src.file_name) }}
                    <span v-if="src.chunk_count && src.chunk_count > 1" class="text-caption text-medium-emphasis">
                      · {{ src.chunk_count }} bölüm
                    </span>
                  </div>
                </div>
                <v-btn
                  icon variant="text" size="small"
                  :disabled="src.available === false"
                  :title="src.available === false ? 'Dosya yerelde bulunamadı' : 'Belgeyi önizle'"
                  @click.stop="openDocumentPreview(src)"
                >
                  <span class="material-symbols-outlined" style="font-size:20px">visibility</span>
                </v-btn>
                <v-btn
                  icon variant="text" size="small"
                  :disabled="src.available === false || downloadingSourceFile === src.file_name"
                  :title="src.available === false ? 'Dosya yerelde bulunamadı' : 'Belgeyi indir'"
                  @click.stop="downloadSourceFile(src)"
                >
                  <span class="material-symbols-outlined" style="font-size:20px">
                    {{ downloadingSourceFile === src.file_name ? 'hourglass_empty' : 'download' }}
                  </span>
                </v-btn>
              </v-sheet>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Belge önizleme modalı (ana chat ekranıyla aynı bileşen) -->
    <DocumentPreviewModal
      v-model="documentPreviewVisible"
      :document="selectedDocument"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from '@/plugins/axios'
import { useToast } from 'vue-toastification'
import DocumentPreviewModal from '@/components/Apps/LawAssistant/DocumentPreviewModal.vue'
import {
  fetchUserQuestions,
  type UserQuestionsResponse,
  type UserQuestionItem,
  type QuestionSource,
  type StatsPeriod,
} from '@/services/adminStatsApi'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL
const toast = useToast()

const route = useRoute()
const username = route.params.username as string
const today = new Date().toISOString().slice(0, 10)

const period     = ref<StatsPeriod>('month')
const customFrom = ref(today)
const customTo   = ref(today)
const loading    = ref(false)
const error      = ref<string | null>(null)
const data       = ref<UserQuestionsResponse | null>(null)
const currentPage = ref(1)
const perPage    = 20

const dialog   = ref(false)
const selected = ref<UserQuestionItem | null>(null)

const documentPreviewVisible = ref(false)
const selectedDocument       = ref<QuestionSource | null>(null)
const downloadingSourceFile  = ref<string | null>(null)

const headers = [
  { title: 'Tarih',  key: 'asked_at',  width: '150px' },
  { title: 'Soru',   key: 'question' },
  { title: 'Puan',   key: 'rating',   width: '120px', sortable: false },
  { title: 'Yorum',  key: 'comment',  width: '200px', sortable: false },
]

async function load(page = 1) {
  loading.value = true
  error.value = null
  currentPage.value = page
  try {
    const dateRange = period.value === 'custom'
      ? { from: customFrom.value, to: customTo.value }
      : undefined
    const res = await fetchUserQuestions(username, period.value, dateRange, page, perPage)
    data.value = res.data
  } catch (e: any) {
    error.value = e?.response?.data?.message ?? e?.message ?? 'Bir hata oluştu'
  } finally {
    loading.value = false
  }
}

function onPeriodChange() {
  if (period.value !== 'custom') load()
}

function onCustomRangeChange() {
  if (customFrom.value && customTo.value && customFrom.value <= customTo.value) load()
}

function goToPage(page: number) {
  load(page)
}

function openDetail(item: UserQuestionItem) {
  selected.value = item
  dialog.value = true
}

function formatDate(dt: string): string {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// --- Kaynak (Dayanak Belgeler) yardımcıları — ana chat ekranıyla aynı mantık ---
function sourceGroups(sources: QuestionSource[]) {
  const groups: Record<string, QuestionSource[]> = { yasatuzuk: [], karar: [], unknown: [] }
  for (const s of sources) {
    const folder = String(s?.folder || '').toLowerCase()
    if (folder === 'yasatuzuk') groups.yasatuzuk.push(s)
    else if (folder === 'karar' || folder === 'kararlar') groups.karar.push(s)
    else groups.unknown.push(s)
  }
  return [
    { key: 'y', title: 'Yasa & Tüzükler', items: groups.yasatuzuk },
    { key: 'k', title: 'Emsal Kararlar', items: groups.karar },
    { key: 'u', title: 'Diğer Kaynaklar', items: groups.unknown },
  ].filter((g) => g.items.length)
}

function kunyeCourt(src: QuestionSource): string | null {
  const c = String(src?.citation || '')
  return c.includes('|') ? c.split('|')[0].trim() : null
}

function kunyeNumbers(src: QuestionSource): string | null {
  const c = String(src?.citation || '')
  if (c.includes('|')) {
    return c.split('|').slice(1)
      .map((s) => s.trim().replace(/^Dağıtım:\s*/i, '').replace(/^Dava No:\s*/i, 'Dava '))
      .filter(Boolean)
      .join(' · ')
  }
  return /\d/.test(c) && c !== src?.file_name ? c : null
}

function formatSourceName(filename?: string): string {
  if (!filename) return '—'
  return filename.replace(/\.(docx?|pdf)$/i, '').replace(/_/g, ' ')
}

// Önizleme: ana chat ile aynı DocumentPreviewModal bileşenini kullanır.
function openDocumentPreview(src: QuestionSource) {
  selectedDocument.value = src
  documentPreviewVisible.value = true
}

// İndirme: ana chat'teki downloadSourceFile ile aynı endpoint/akış.
async function downloadSourceFile(src: QuestionSource) {
  const fileName = src?.file_name
  if (!fileName) {
    toast.error('Dosya adı bulunamadı')
    return
  }
  downloadingSourceFile.value = fileName
  try {
    const fileId = src.file_id || 'local'
    const response = await axios.get(`${API_BASE_URL}/files/${fileId}/download`, {
      params: { filename: fileName },
      responseType: 'blob',
    })
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toast.success('Dosya indiriliyor...')
  } catch (err: any) {
    if (err?.response?.status === 404) {
      toast.warning('Dosya bulunamadı. Lütfen mevzuat.mahkemeler.net adresinden kontrol ediniz.', { timeout: 6000 })
    } else {
      toast.error('Dosya indirilirken bir hata oluştu.')
    }
  } finally {
    downloadingSourceFile.value = null
  }
}

onMounted(() => load())
</script>

<style scoped>
.question-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 400px;
}

.answer-content :deep(p)      { margin-bottom: 0.5rem }
.answer-content :deep(ul),
.answer-content :deep(ol)     { padding-left: 1.5rem; margin-bottom: 0.5rem }
.answer-content :deep(h1),
.answer-content :deep(h2),
.answer-content :deep(h3)     { font-size: 1rem; font-weight: 600; margin: 0.75rem 0 0.25rem }
.answer-content :deep(strong) { font-weight: 600 }
.answer-content :deep(code)   { background: rgba(0,0,0,0.06); padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.85em }
</style>
